import { useMemo, useRef, useState } from 'react'
import styles from './EntityMultiSelect.module.css'

import clsx from 'clsx'
import { motion, AnimatePresence } from 'framer-motion'
import { useDebounce } from '../../../hooks/useDebounce'
import { Input } from '../../controls/Input/Input'
import Checkbox from '../../controls/Checkbox/Checkbox'

interface BaseEntity {
  id: number
}

interface Props<T extends BaseEntity> {
  value: number[]
  onChange: (ids: number[]) => void

  useInfiniteHook: (params: any) => any
  searchParamName: string
  limit?: number

  getLabel: (item: T) => string

  disabled?: boolean
  className?: string
  itemClassName?: string
  query?: any

  markedIds?: number[]
  onMarkedChange?: (ids: number[]) => void
  renderMarkIcon?: (marked: boolean) => React.ReactNode
}

export function EntityMultiSelect<T extends BaseEntity>({
  value,
  onChange,
  useInfiniteHook,
  searchParamName,
  limit = 20,
  getLabel,
  disabled,
  className,
  itemClassName,
  query,
  markedIds = [],
  onMarkedChange,
  renderMarkIcon,
}: Props<T>) {
  const [search, setSearch] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  const debounced = useDebounce(search, 400)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteHook({
    limit,
    ...query,
    [searchParamName]: debounced || undefined,
  })

  const items: T[] = data?.items ?? []

  const sortedItems = useMemo(() => {
    if (!value.length) return items

    const selected = new Set(value.map(Number))

    return [
      ...items.filter((i) => selected.has(Number(i.id))),
      ...items.filter((i) => !selected.has(Number(i.id))),
    ]
  }, [items, value])

  const toggle = (id: number) => {
    if (disabled) return

    const numId = Number(id)
    const next = value.map(Number).includes(numId)
      ? value.filter((x) => Number(x) !== numId)
      : [...value, numId]

    onChange(next)
  }

  const toggleMark = (id: number) => {
    if (!onMarkedChange) return

    const numId = Number(id)
    const isMarked = markedIds.map(Number).includes(numId)

    const nextMarked = isMarked
      ? markedIds.filter((x) => Number(x) !== numId)
      : [...markedIds, numId]

    onMarkedChange(nextMarked)

    if (!isMarked && !value.map(Number).includes(numId)) {
      onChange([...value, numId])
    }
  }

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasNextPage || isFetchingNextPage) return

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      fetchNextPage()
    }
  }

  const count = value.length

  return (
    <div className={clsx(styles.main, className)}>
      <div className={styles.header}>
        {!disabled ? (
          <Input
            placeholder="Suchen…"
            value={search}
            onChange={setSearch}
            clearable
          />
        ) : <span />}

        <span
          className={clsx(styles.countChip, count === 0 && styles.countChipEmpty)}
          aria-label={`${count} ausgewählt`}
        >
          {count} ausgewählt
        </span>
      </div>

      <motion.div
        ref={listRef}
        className={styles.list}
        onScroll={handleScroll}
        layout
      >
        {isLoading ? (
          <p className={styles.status}>Wird geladen…</p>
        ) : sortedItems.length === 0 ? (
          <p className={styles.status}>Keine Ergebnisse</p>
        ) : (
          <AnimatePresence initial={false}>
            {sortedItems.map((item) => {
              const checked = value.map(Number).includes(Number(item.id))
              const marked = markedIds.map(Number).includes(Number(item.id))

              return (
                <motion.div
                  key={item.id}
                  layout
                  transition={{ layout: { duration: 0.25 } }}
                  onClick={() => toggle(item.id)}
                  className={clsx(
                    styles.row,
                    checked && styles.rowSelected,
                    itemClassName,
                  )}
                >
                  <div className={styles.rowLeft}>
                    <span className={clsx(styles.label, checked && styles.labelSelected)}>
                      {getLabel(item)}
                    </span>
                    {onMarkedChange && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleMark(item.id)
                        }}
                        className={clsx(
                          styles.markIcon,
                          marked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                        )}
                      >
                        {renderMarkIcon ? (
                          renderMarkIcon(marked)
                        ) : (
                          <span
                            className={clsx(
                              'text-lg',
                              marked ? 'text-indigo-600' : 'text-gray-400',
                            )}
                          >
                            ★
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    onClick={(e) => e.stopPropagation()}
                    className={clsx(
                      styles.checkboxWrap,
                      checked && styles.checkboxWrapSelected,
                    )}
                  >
                    <Checkbox checked={checked} onChange={() => toggle(item.id)} />
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        )}
      </motion.div>
    </div>
  )
}
